import { Box, Button, Card, CardContent, Skeleton, Stack, Typography } from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { useQueryParam } from "use-query-params";

import { EscrowLedger } from "../../reports/escrowLedgers/types";
import { LedgerTransaction } from "../types";
import { OrderInfoStack } from "./styles";
import { processLedgerData } from "../../reports/escrowLedgers/processLedgerData";
import ClosingChecklistEscrow from "../ClosingChecklistEscrow";
import ClosingChecklistLedgers from "../ClosingChecklistLedgers";
import LedgerLoader from "./LedgerLoader";
import LedgerSummary from "../LedgerSummary";
import Order from "./Order";
import Properties from "./Properties";
import { OrderInfo } from "../../shared/orderInfo/types";

const TaskOrderInfo = ({ orderInfo, isCondensed }: { orderInfo: OrderInfo | null; isCondensed: boolean }) => {
  const [orderNumber] = useQueryParam<string>("order");
  const [openClosingChecklistEscrow, setOpenClosingChecklistEscrow] = useState<boolean>(false);
  const [escrowLedgers, setEscrowLedgers] = useState<LedgerTransaction[]>([]);
  const [escrowLedgerTotal, setEscrowLedgerTotal] = useState<number | null>(null);
  const [isLedgersLoading, setIsLedgersLoading] = useState<boolean>(true);

  const fetchEscrowLedgers = async () => {
    setIsLedgersLoading(true);
    if (!orderNumber) {
      return;
    }
    const { data } = await axios.get<EscrowLedger[]>(`/proxy/api/ledgers/getescrowledgers`);
    const orderNumberLowercased = orderNumber.toLowerCase();
    const ledgers = processLedgerData(data);
    const transactions = ledgers
      .filter((d) => d.name.toLowerCase().startsWith(orderNumberLowercased))
      .map((d) => {
        const splitName = d.name.split("-");
        return {
          name: splitName[splitName.length - 1],
          amount: d.amount
        };
      })
      .sort((a, b) => {
        const numA = parseInt(a.name.replace(/\D/g, ""), 10);
        const numB = parseInt(b.name.replace(/\D/g, ""), 10);
        return numA - numB;
      }) as LedgerTransaction[];
    setEscrowLedgers(transactions);
    const escrowTotal = transactions.reduce((total, transaction) => {
      return total + transaction.amount;
    }, 0);
    setEscrowLedgerTotal(escrowTotal);
    setIsLedgersLoading(false);
  };

  useEffect(() => {
    fetchEscrowLedgers();
  }, [orderNumber]);

  return (
    <>
      <Stack direction="row" marginBottom={0} sx={{ overflow: "auto" }}>
        <Card
          sx={{
            backgroundColor: isCondensed ? "rgba(0, 0, 0, 0.02)" : "transparent",
            borderRadius: 0,
            borderRight: isCondensed ? "1px solid rgba(0, 0, 0, 0.18)" : "none",
            boxShadow: 0,
            overflow: "auto",
            padding: 0,
            width: "100%",
            fontSize: { sm: "0.7rem", lg: "0.875rem" }
          }}
        >
          <CardContent sx={{ padding: 0 }} style={{ paddingBottom: 0 }}>
            <OrderInfoStack
              direction={isCondensed ? "column" : "row"}
              spacing={isCondensed ? 0 : 3}
              isCondensed={isCondensed}
            >
              <Stack direction="column" sx={{ justifyContent: "space-between" }}>
                <Stack direction="row" alignItems="flex-start" className="customCard customCard-blue">
                  <Typography
                    sx={{
                      fontSize: { xs: "1rem", lg: "1.3rem" }
                    }}
                    fontWeight={600}
                    color="primary"
                  >
                    {orderInfo?.titleCode} {orderInfo?.number}
                    {!orderInfo && <Skeleton height={40} width={200} />}
                  </Typography>
                </Stack>
                <Order />
              </Stack>
              <Properties />
              <Stack className="customCard" style={{ borderBottom: "none" }}>
                <ClosingChecklistLedgers
                  stackLedgers={isCondensed}
                  escrowContent={(
                    <Box>
                      <Button
                        variant="outlined"
                        onClick={() => setOpenClosingChecklistEscrow(true)}
                        size="small"
                        sx={{
                          marginBottom: 1,
                          fontSize: { sm: "0.68rem", lg: "0.875rem" }
                        }}
                      >
                        Transfer Escrow Funds
                      </Button>
                      {openClosingChecklistEscrow && (
                        <ClosingChecklistEscrow
                          onClose={() => setOpenClosingChecklistEscrow(false)}
                          refresh={fetchEscrowLedgers}
                        />
                      )}
                      {!isLedgersLoading && escrowLedgers.length > 0 && (
                        <LedgerSummary
                          ledgerTypeHeader={"Escrow Ledgers"}
                          total={escrowLedgerTotal}
                          transactions={escrowLedgers}
                          orderNumber={orderNumber}
                        />
                      )}
                      {isLedgersLoading && <LedgerLoader />}
                    </Box>
                  )}
                />
              </Stack>
            </OrderInfoStack>
          </CardContent>
        </Card>
      </Stack>
    </>
  );
};

export default TaskOrderInfo;
